import React, { useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import { useTranslation } from "react-i18next";

import { Rating, RatingChangeEvent } from "primereact/rating";

import { assetService } from "../services/assetService";
import { partnerAliveService } from "../services/partnerAliveService";
import { Azay, partnerAzayService } from "../services/partnerAzayService";
import {
  Interpreter,
  partnerInterpreterService,
} from "../services/partnerInterpreterService";
import { partnerMTLService } from "../services/partnerMtlService";
import { Partner, partnerService } from "../services/partnerService";
// import { systemService } from "../services/systemService";
import { transactionService } from "../services/transactionService";

import Review from "../model/Review";

const ReviewPage: React.FC = () => {
  const pageKey = "review";

  // const { partnerMtlIsActive } = usePartnerMTL();

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [logo, setLogo] = useState<string>();
  const [partner, setPartner] = useState<Partner | undefined>();
  const [comment, setComment] = useState("");
  const [pointState, setPointState] = useState<Review>({
    pointNumQ1: 5,
    pointNumQ2: 5,
  });

  const language = localStorage.getItem("lang");

  // --- Mount logic ---
  useEffect(() => {
    const init = async () => {
      const partnerData = partnerService.getPartner();
      setPartner(partnerData);

      if (!partnerData) {
        navigate("/");
        return;
      }

      // Partner: Alive.
      if (partnerAliveService.isActive()) {
        const deepLinkUrl = partnerAliveService.getDeepLinkUrl();
        const transactionStatus = transactionService.getTransactionStatus();
        if (
          transactionStatus === 7 ||
          transactionStatus === 10 ||
          transactionStatus === 11
        ) {
          // Redirect to web payment url with deep link url attached via query param.
          const webPaymentUrl = process.env.REACT_APP_WEB_PAYMENT_URL!;
          const encodedDeepLinkUrl = encodeURIComponent(deepLinkUrl);
          window.location.href = `${webPaymentUrl}?partner=ALive&deepLinkUrl=${encodedDeepLinkUrl}`;
        } else {
          // Redirect to deep link url.
          window.location.href = deepLinkUrl;
        }
        return;
      }

      if (partnerData) {
        setLogo(assetService.getLogo(partnerData.name));
        // i18n.changeLanguage(systemService.getSystem().language);
      }
    };

    init();
  }, [i18n, navigate]);

  // --- Handlers ---
  const sendReview = async () => {
    const satisfactionRating = pointState.pointNumQ1;
    const recommendationRating = pointState.pointNumQ2;

    await transactionService.rateTransaction(
      satisfactionRating,
      recommendationRating,
      comment
    );

    if (partnerAzayService.isActive()) {
      window.location.href = (partner as Azay).returnUrl;
      return;
    }

    // Partner: Interpreter
    if (partnerInterpreterService.isActive()) {
      const interpreterLanguage = (partner as Interpreter).interpreterLanguage;

      await navigate(
        `/index?partner=Interpreter&interpreterLanguage=${interpreterLanguage}&language=${language}`
      );
    }

    const transactionStatus = transactionService.getTransactionStatus();

    if (partnerMTLService.isActive()) {
      localStorage.setItem("source", "MTL");
      localStorage.setItem("lang", "TH");
      if (
        transactionStatus === 7 ||
        transactionStatus === 10 ||
        transactionStatus === 11
      ) {
        window.location.href = process.env.REACT_APP_WEB_PAYMENT_URL!;
      } else {
        window.location.href = process.env.REACT_APP_WEB_PAYMENT_SUCCESS_URL!;
      }
      return;
    }

    if (
      transactionStatus === 7 ||
      transactionStatus === 10 ||
      transactionStatus === 11
    ) {
      window.location.href = process.env.REACT_APP_WEB_PAYMENT_URL!;
    } else {
      navigate("/success");
    }
  };

  return (
    <div className="flex flex-col items-center p-6 gap-y-4 justify-center min-h-screen w-full">
      <div className="w-full max-w-3xl border px-4 py-6 md:p-10 rounded-3xl shadow-md bg-white relative">
        {/* Logo */}
        <div className="flex justify-center items-center">
          {logo && (
            <img
              src={logo}
              alt="Logo"
              className="w-[172px] h-[64px] object-contain"
            />
          )}
        </div>

        {/* Question 1 + 2 */}
        <div className="bg-white rounded-lg px-6 py-3 mt-6 w-full">
          <div>
            <h2 className="text-gray-800 font-medium">
              {t("pages.review.texts.question1Thai")}
            </h2>
            <p className="text-gray-600">{t("pages.review.texts.question1")}</p>
            <div className="flex justify-center my-4">
              <Rating
                value={pointState.pointNumQ1}
                onChange={(e: RatingChangeEvent) =>
                  setPointState((prev) => ({
                    ...prev,
                    pointNumQ1: e.value ?? 0,
                  }))
                }
                cancel={false}
                className="custom-rating"
              />
            </div>

            <h2 className="text-gray-800 font-medium mt-6">
              {t("pages.review.texts.question2Thai")}
            </h2>
            <p className="text-gray-600">{t("pages.review.texts.question2")}</p>
            <div className="flex justify-center my-4">
              <Rating
                value={pointState.pointNumQ2}
                onChange={(e: RatingChangeEvent) =>
                  setPointState((prev) => ({
                    ...prev,
                    pointNumQ2: e.value ?? 0,
                  }))
                }
                cancel={false}
                className="custom-rating"
              />
            </div>
          </div>
        </div>

        {/* Suggestions */}
        <div className="bg-white border border-gray-300 rounded-lg p-6 mt-4 w-full">
          <h2 className="text-gray-800 font-medium">
            {t("pages.review.texts.suggestions")}
          </h2>
          <textarea
            id="comment"
            rows={4}
            className="w-full border border-gray-300 rounded-lg p-3 mt-3 focus:ring-2 focus:ring-blue-500 focus:outline-none"
            placeholder={t("pages.review.texts.suggestionsPlaceholder")}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        </div>

        {/* Submit */}
        <div className="mt-6 w-full">
          <button
            onClick={sendReview}
            className="submit-review-btn"
            // className="w-full bg-[#e0bb84] text-black py-3 rounded-3xl hover:bg-[#c19e67] transition-colors !text-sm md:!text-base"
          >
            {t("pages.review.buttons.submit")}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewPage;
